import { THEME } from "Util/constants";
import {
  Button,
  DatePicker,
  Dropdown,
  Form,
  Input,
  InputNumber,
  notification,
  Select,
  Switch,
  Table,
  Tag,
  TimePicker,
} from "antd";
import moment from "moment-timezone";
import { DAYS, DAY_LABELS, isDriverActive, isValidRange } from "Util/driverSchedule";
import { DriverBookingStatus, DriverBookingStatusColors, DriverBookingStatusLabels } from "Util/constants";
import User from "Hooks/user";
import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import { useLazyQuery, useMutation } from "@apollo/client";
import CommonButton, { ButtonLoader } from "Components/CommonButton";
import CommonDiv from "Components/CommonDiv";
import PhoneInput from 'Components/PhoneValidator';
import DepotsDropDown from 'Components/DepotsDropdown';
import { UPSERT_DRIVERS, UPDATE_DRIVER_BOOKING_STATUS } from "Util/mutations";
import { useHistory } from "react-router-dom";
import { useRecoilState } from 'recoil';
import { driverDetailAtom } from 'Store/logisticsAtom';
import { Header } from "./components/DriverCard";
import { GET_DRIVER } from "Util/queries";
import { addDriver, updateDriver, getDriver } from 'Util/trackpod'
import { PlusOutlined } from '@ant-design/icons';
import DriverTypeDropDown from "./components/DriverCard/DriverTypeDropdown";
import { Country, City } from 'country-state-city';

const Container = styled.div``;

const SectionContainer = styled.div`
	.content {
		margin: 5px;
		display: flex;
		justify-content: space-between;
		& > div.division {
			width: calc(50% - 30px);
			.ant-form-item-label > label {
				width: 180px;
			}
			.ant-form-item-control {
				width: 10px;
			}
		}
	}
	.division-title {
		font-weight: bold;
	}

	.ant-switch-checked {
		background-color: ${THEME.colors.primary};
	}
`;

const Section = (props) => {
  return (
    <SectionContainer>
      <h3 style={{ borderBottom: "solid thin #000", padding: "5px" }}>
        {props.title}
      </h3>
      <div className="content">{props.children}</div>
    </SectionContainer>
  );
};

function DriverCard(props) {
  const [form] = Form.useForm();
  const params = useParams();
  const routeLocation = useLocation();
  const history = useHistory();
  const editMode = routeLocation.pathname.includes("/logistics/drivers/edit");
  const [formLoading, setFormLoading] = useState(false);
  const [img] = useState();
  // const [origImg, setOrigImg] = useState(null);
  // const imageRef = useRef(null)
  const [cityList, setCityList] = useState([])
  const [driverDetail, setDriverDetail] = useRecoilState(driverDetailAtom)
  const [ setDisabled] = useState(editMode)
  const [loc, setLoc] = useState({
    country: ''
})


  const [update] = useMutation(UPSERT_DRIVERS, {
    onCompleted(data) {
      notification['success']({
        message: 'Saved!',
        description: `Driver has been saved!`,
      });
    }
  })

  const [updateBookingStatus, { loading: bookingLoading }] = useMutation(UPDATE_DRIVER_BOOKING_STATUS)
  const { user_id } = User()

  const bookingStatus = driverDetail?.booking_status ?? DriverBookingStatus.AVAILABLE
  const [activeNow, setActiveNow] = useState(() => isDriverActive(driverDetail?.active_schedule))

  // Recompute availability every minute so an open page flips at the schedule boundary.
  useEffect(() => {
    setActiveNow(isDriverActive(driverDetail?.active_schedule))
    const t = setInterval(() => setActiveNow(isDriverActive(driverDetail?.active_schedule)), 60_000)
    return () => clearInterval(t)
  }, [driverDetail?.active_schedule])

  const changeBookingStatus = (to_status, reason) => {
    if (!driverDetail?.id || to_status === bookingStatus) return
    updateBookingStatus({
      variables: {
        driver_id: driverDetail.id,
        to_status,
        from_status: bookingStatus,
        admin_user_id: user_id ? parseInt(user_id) : null,
        reason: reason ?? null,
      }
    }).then(({ data }) => {
      const newRow = data?.insert_driver_status_history_one
      setDriverDetail(prev => ({
        ...prev,
        booking_status: to_status,
        status_history: [newRow, ...(prev?.status_history ?? [])].filter(Boolean),
      }))
      notification.success({
        message: 'Status updated',
        description: `Driver is now ${DriverBookingStatusLabels[to_status]}.`,
      })
    }).catch(err => {
      notification.error({ message: 'Status update failed', description: err.message })
    })
  }

  const [getDriverDetails] = useLazyQuery(GET_DRIVER, {
    fetchPolicy: "network-only",
    onCompleted(data) {
        const userDetails = data.drivers[0]

        setDriverDetail(userDetails)

        const {address1, address2, city, country, active_schedule} = userDetails
        const address = [address1, address2, city, country].filter(Boolean).join(", ");

        const scheduleFields = {}
        DAYS.forEach(day => {
          const range = active_schedule?.[day]?.[0]
          scheduleFields[`schedule_${day}`] = range
            ? [moment(range.start, 'HH:mm'), moment(range.end, 'HH:mm')]
            : null
        })

        form.setFieldsValue({
            ...userDetails,
            ...scheduleFields,
            address
        })
    }
  })

  useEffect(() => {
    if (editMode) {
      getDriverDetails({ variables: { where: { id: { _eq: params.id } } }, });
    } 
    setCityList(City.getCitiesOfCountry('IN'));
  }, [getDriverDetails, params.id, editMode]);

  const submitHandler = async (values) => {

    setFormLoading(true)

    // Pull schedule_<day> form fields out and pack into JSONB active_schedule.
    const active_schedule = {}
    DAYS.forEach(day => {
      const range = values[`schedule_${day}`]
      active_schedule[day] = range && range[0] && range[1]
        ? [{ start: range[0].format('HH:mm'), end: range[1].format('HH:mm') }]
        : []
    })

    const {
      dimensions, address,
      schedule_mon, schedule_tue, schedule_wed, schedule_thu, schedule_fri, schedule_sat, schedule_sun,
      ...rest
    } = values;
    const driverDetails = { ...rest, active_schedule }
    let uuid = null

    const updateObject = {
      "Id": values.uuid,
      "Phone": values.phone,
      "HomeAddress": address,
      "Name": values.name,
      "Active": values.active,
      "Username": values.username,
      "Password": values.password
    }

    const addObject = {
      "Phone": values.phone,
      "HomeAddress": address,
      "Name": values.name,
      "Active": values.active,
      "Username": values.username,
      "Password": values.password
    }

    const getObject = {
      "Username": values.username
    }
  
    if (editMode){
      try {
        const res = await updateDriver(updateObject)
        const { Detail, Status, Title } = res

        if(Status !== 202){
          notification["error"]({
            message: Title,
            description: Detail,
          });
          setFormLoading(false)
          history.goBack()
          return
        }
      } catch (error) {
        if (error.response) {
          console.error('Error status code:', error.response.status);
          if (error.response.status === 405) {
              console.error('Error 405: Method Not Allowed');
          } else if (error.response.status === 500) {
              console.error('Error 500: Internal Server Error');
          }
      } else {
          console.error('Error occurred:', error);
      }
        notification["error"]({
          message: "Something went wrong!",
          description: "Please try again later.",
        });
        setFormLoading(false)
        history.goBack()
        return
      }

    }
    else{
      try {
        const res = await addDriver( addObject )
        const { Detail, Status, Title } = res

        if(Status !== 201){
          notification["error"]({
            message: Title,
            description: Detail,
          });
          setFormLoading(false)
          history.goBack()
          return
        }

        const getRes = await getDriver(getObject)        
        uuid = getRes.Id

      } catch (error) {
        if (error.response) {
          console.error('Error status code:', error.response.status);
          if (error.response.status === 405) {
              console.error('Error 405: Method Not Allowed');
          } else if (error.response.status === 500) {
              console.error('Error 500: Internal Server Error');
          }
      } else {
          console.error('Error occurred:', error);
      }
        notification["error"]({
          message: "Something went wrong!",
          description: "Please try again later.",
        });
        setFormLoading(false)
        history.goBack()
        return
      }
    }

    const objects = {
      ...driverDetails,
      uuid
    }

    await update({ variables: { 
      update_columns: ["id", ...Object.keys(driverDetails)],
      objects 
      } 
    })

    setFormLoading(false)
    history.goBack()
  };

  const failedSubmitHandler = (values) => {
    setFormLoading(false);
    notification["error"]({
      message: "Something went wrong!",
      description: "Please input values on required fields.",
    });
  };

  const handleAddressChange = () => {
    const { address1, address2, country, city } = form.getFieldsValue(['address1', 'address2', 'country', 'city']);
    const address = [address1, address2, city, country].filter(Boolean).join(", ");
    form.setFieldsValue({ address });
  };

  return (
    <Container>
      <Header editMode={editMode} setDisabled={setDisabled} onEvent={() => {}} />
      <Form
        form={form}
        name="vendorForm"
        onFinish={submitHandler}
        onFinishFailed={failedSubmitHandler}
      >
        <Section title="General">
          <div className="division">

            <Form.Item name="id" hidden >
              <Input />
            </Form.Item>

            <Form.Item name="uuid" hidden >
              <Input />
            </Form.Item>

            <Form.Item label="Driver License No." name="license">
              <Input />
            </Form.Item>

            <Form.Item label="License Expiry" name="license_expiry">
              <DatePicker format="DD MMM YYYY" style={{ width: '100%' }}/>
            </Form.Item>


            <Form.Item label="Driver Type" name="type">
              <DriverTypeDropDown />
            </Form.Item>

            <Form.Item label="Depot" name="depot_id" rules={[{ required: true, message: 'Depot is required' }]}>
              <DepotsDropDown />
            </Form.Item>


            <Form.Item label="Name" name="name" rules={[{ required: true, message: '' }]}>
              <Input />
            </Form.Item>

            <Form.Item 
                name="active" 
                label="Blocked" 
                valuePropName="checked" 
                initialValue={false}
            >
                <Switch />
            </Form.Item>

            <Form.Item name="address" hidden>
              <Input />
            </Form.Item>

            <Form.Item label="Address 1" name="address1">
              <Input onChange={handleAddressChange}/>
            </Form.Item>

            <Form.Item label="Address 2" name="address2">
              <Input onChange={handleAddressChange}/>
            </Form.Item>

            <Form.Item label="Country" name="country" rules={[{ required: true, message: '' }]} initialValue={"India"}>
              <Select showSearch
                  onChange={(countryName, option) => {
                    const isoCode = option.key
                    setCityList(City.getCitiesOfCountry(isoCode));
                    setLoc({ ...loc, country: isoCode });
                    handleAddressChange();
                  }}
              
              >
                  {Country.getAllCountries().map(v => <Select.Option value={v.name} key={`${v.isoCode}`}>{v.flag} {v.name} ({v.isoCode})</Select.Option>)}
              </Select>
            </Form.Item>

            <Form.Item label="City" name="city" rules={[{ required: true, message: '' }]}>
                <Select
                    showSearch
                    placeholder="Select a City"
                    onChange={handleAddressChange}
                >
                    {cityList.map(v => <Select.Option value={v.name} key={`city-${v.name}`}>{v.name}</Select.Option>)}
                </Select>

            </Form.Item>

            <Form.Item label="Postal Code" name="postal">
              <Input />
            </Form.Item>

            <Form.Item label="Track POD Username" name="username" rules={[{ required: true, message: '' }]}>
              <Input />
            </Form.Item>

            <Form.Item label="Track POD Password" name="password" rules={[{ required: true, message: '' }]}>
              <Input type="password"/>
            </Form.Item>


          </div>

          <div className="division">

          <Form.Item label="Class of Vehicle">
              <div  style={{ display: 'flex', flexDirection: 'column', gap: 8}}>
                <Form.Item name={['dimensions', "length"]} noStyle initialValue={"MCWG"}>
                  <Select/>
                </Form.Item>
                <Form.Item name={['dimensions', "width"]} noStyle initialValue={"LMV"}>
                  <Select/>
                </Form.Item>
                <Form.Item name={['dimensions', "height"]} noStyle initialValue={"TRANS"}>
                  <Select/>
                </Form.Item>
              </div>
            </Form.Item>

            <Form.Item
              label="Max Payload (kg)"
              name="max_payload_capacity"
              initialValue={1750}
              rules={[
                { required: true, message: 'Payload capacity is required' },
                { type: 'number', min: 1, message: 'Must be greater than 0' }
              ]}
            >
              <InputNumber min={1} style={{ width: '100%' }} addonAfter="kg" />
            </Form.Item>

            <Form.Item label="Mobile No." name="phone">
              <PhoneInput />
            </Form.Item>

            <Form.Item label="Email" name="email" >
              <Input />
            </Form.Item>

            <Form.Item label="Aadhaar ID" name="aadhaar_id" >
              <Input />
            </Form.Item>

            <div style={{ display: "flex", flexDirection: "row", marginBottom: 24 }}>
                <div style={{ marginRight: 30 }}>Verification Documents</div>
                <div style={{ display: "flex" }}>
                    {img && <img src={img} alt="" style={{ height: 70, width: 70, marginRight: 10 }} />}
                    <label htmlFor="upload-photo" style={{ display: "flex", backgroundColor: "#D9E3F3", alignItems: "center", justifyContent: "center" }}>
                        <PlusOutlined style={{ fontSize: 70, color: "#90ABDD" }} size={100} color="red" />
                    </label>
                    {/* <input type="file" ref={imageRef} onChange={handleInputChange} id="upload-photo" style={{ display: "none" }} disabled={disabled} /> */}
                </div>
            </div>
                          
            <div style={{ display: "flex", flexDirection: "row", marginBottom: 24 }}>
                <div style={{ marginRight: 52 }}>Add Picture or Logo</div>
                <div style={{ display: "flex" }}>
                    {img && <img src={img} alt="" style={{ height: 70, width: 70, marginRight: 10 }} />}
                    <label htmlFor="upload-photo" style={{ display: "flex", backgroundColor: "#D9E3F3", alignItems: "center", justifyContent: "center" }}>
                        <PlusOutlined style={{ fontSize: 70, color: "#90ABDD" }} size={100} color="red" />
                    </label>
                    {/* <input type="file" ref={imageRef} onChange={handleInputChange} id="upload-photo" style={{ display: "none" }} disabled={disabled} /> */}
                </div>
            </div>

          </div>

        </Section>

        <Section title="Working Schedule">
          <div className="division" style={{ width: '100%' }}>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              Configure active collection times per day. Leave a day blank for "off duty".
              Overnight ranges (end &le; start) are not supported.
            </div>
            {DAYS.map(day => (
              <Form.Item
                key={day}
                label={DAY_LABELS[day]}
                name={`schedule_${day}`}
                rules={[{
                  validator: (_, value) => {
                    if (!value || (!value[0] && !value[1])) return Promise.resolve()
                    if (!value[0] || !value[1]) return Promise.reject(new Error('Both start and end are required'))
                    if (!isValidRange(value[0].format('HH:mm'), value[1].format('HH:mm'))) {
                      return Promise.reject(new Error('End must be after start (overnight not supported)'))
                    }
                    return Promise.resolve()
                  }
                }]}
              >
                <TimePicker.RangePicker format="HH:mm" minuteStep={15} style={{ width: 260 }} />
              </Form.Item>
            ))}
          </div>
        </Section>

        {editMode && (
          <Section title="Status">
            <div className="division" style={{ width: '100%' }}>
              <Form.Item label="Availability">
                <Tag color={activeNow ? 'green' : 'default'} style={{ padding: '2px 10px' }}>
                  {activeNow ? 'Active' : 'Off Duty'}
                </Tag>
              </Form.Item>
              <Form.Item label="Booking Status">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Tag color={DriverBookingStatusColors[bookingStatus]} style={{ padding: '2px 10px' }}>
                    {DriverBookingStatusLabels[bookingStatus]}
                  </Tag>
                  <Dropdown
                    disabled={bookingLoading}
                    menu={{
                      items: [
                        {
                          key: DriverBookingStatus.AVAILABLE,
                          label: 'Reset to Available',
                          disabled: bookingStatus === DriverBookingStatus.AVAILABLE,
                          onClick: () => changeBookingStatus(DriverBookingStatus.AVAILABLE, 'Manual reset'),
                        },
                        {
                          key: DriverBookingStatus.INACTIVE,
                          label: 'Mark Inactive',
                          disabled: bookingStatus === DriverBookingStatus.INACTIVE,
                          onClick: () => changeBookingStatus(DriverBookingStatus.INACTIVE, 'Manually marked inactive'),
                        },
                        {
                          key: DriverBookingStatus.COMPLETED,
                          label: 'Mark Completed',
                          disabled: bookingStatus === DriverBookingStatus.COMPLETED,
                          onClick: () => changeBookingStatus(DriverBookingStatus.COMPLETED, 'Manually marked completed'),
                        },
                      ]
                    }}
                  >
                    <Button size='small' loading={bookingLoading}>Change Status</Button>
                  </Dropdown>
                </div>
              </Form.Item>
            </div>
          </Section>
        )}

        {editMode && (
          <Section title="Status History">
            <div className="division" style={{ width: '100%' }}>
              <Table
                size="small"
                rowKey="id"
                pagination={{ pageSize: 10, showSizeChanger: false }}
                dataSource={driverDetail?.status_history ?? []}
                locale={{ emptyText: 'No status changes yet.' }}
                columns={[
                  {
                    title: 'When',
                    dataIndex: 'created_at',
                    key: 'created_at',
                    render: v => moment(v).tz('Asia/Kolkata').format('DD MMM YYYY HH:mm'),
                    width: 180,
                  },
                  {
                    title: 'From',
                    dataIndex: 'from_status',
                    key: 'from_status',
                    render: v => v
                      ? <Tag color={DriverBookingStatusColors[v]}>{DriverBookingStatusLabels[v] ?? v}</Tag>
                      : <span style={{ color: '#bbb' }}>—</span>,
                    width: 120,
                  },
                  {
                    title: 'To',
                    dataIndex: 'to_status',
                    key: 'to_status',
                    render: v => <Tag color={DriverBookingStatusColors[v]}>{DriverBookingStatusLabels[v] ?? v}</Tag>,
                    width: 120,
                  },
                  {
                    title: 'By',
                    key: 'by',
                    render: (_v, r) => r.admin_user?.full_name ?? <span style={{ color: '#bbb' }}>System</span>,
                    width: 160,
                  },
                  { title: 'Reason', dataIndex: 'reason', key: 'reason' },
                ]}
              />
            </div>
          </Section>
        )}

        <CommonDiv direction="row" justify="flex-end">
          <ButtonLoader loading={formLoading} htmlType="submit" >
            {editMode ? "Update" : "Save"}
          </ButtonLoader>

          <CommonButton className='reverse' onClick={() => history.goBack()}>
            Cancel
          </CommonButton>

        </CommonDiv>
      </Form>
    </Container>
  );
}

DriverCard.propTypes = {};

export default DriverCard;
